import type { FC, ReactNode } from 'react'
import type { PersonSummary } from '@/types'

import { cn } from '@/lib/utils'

type AvatarSize = 'sm' | 'md' | 'lg' | 'xl'

interface PersonAvatarProps {
    person: Pick<PersonSummary, 'name' | 'photo' | 'photo_alt'>
    size?: AvatarSize
    className?: string
}

const SIZES: Record<AvatarSize, string> = {
    sm: 'size-11 text-sm',
    md: 'size-14 text-base',
    lg: 'size-24 text-2xl',
    xl: 'size-40 text-5xl md:size-48'
}

const initialsOf = (name: string): string => {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
}

const PersonAvatar: FC<PersonAvatarProps> = ({ person, size = 'md', className }): ReactNode => {
    return (
        <span
            className={cn(
                'relative grid shrink-0 place-items-center overflow-hidden rounded-full border-2 border-linen bg-forest-soft font-heading font-semibold text-forest ring-1 ring-line',
                SIZES[size],
                className
            )}
        >
            {person.photo ? (
                <img
                    src={person.photo}
                    alt={person.photo_alt || person.name}
                    loading='lazy'
                    className='size-full object-cover'
                />
            ) : (
                <span aria-hidden='true'>
                    {initialsOf(person.name)}
                </span>
            )}
        </span>
    )
}

export default PersonAvatar
